/**
 * React hooks for Umami analytics tracking
 */

import { useEffect, useRef } from 'react';
import { track, trackPageView } from './core';

export function usePageTracking(pagePath: string, pageTitle: string): void {
  const tracked = useRef(false);

  useEffect(() => {
    if (!tracked.current) {
      trackPageView(pagePath, pageTitle);
      tracked.current = true;
    }
  }, [pagePath, pageTitle]);
}

export function useEngagementTracking(): void {
  const startTime = useRef(0);
  const totalEngaged = useRef(0);
  const isVisible = useRef(true);

  useEffect(() => {
    startTime.current = Date.now();
    const handleVisibilityChange = () => {
      if (document.hidden) {
        totalEngaged.current += Date.now() - startTime.current;
        isVisible.current = false;
      } else {
        startTime.current = Date.now();
        isVisible.current = true;
      }
    };

    const handleBeforeUnload = () => {
      if (isVisible.current) {
        totalEngaged.current += Date.now() - startTime.current;
      }
      track('engagement_time', {
        engagement_time_msec: totalEngaged.current,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
}

export function useScrollDepthTracking(enabled: boolean = true): void {
  const maxScroll = useRef(0);
  const milestones = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled) return;

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const scrollPercent = Math.round(
          (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
        );

        if (scrollPercent > maxScroll.current) {
          maxScroll.current = scrollPercent;

          const milestonesToTrack = [25, 50, 75, 90, 100];
          for (const milestone of milestonesToTrack) {
            if (scrollPercent >= milestone && !milestones.current.has(milestone)) {
              milestones.current.add(milestone);
              track('scroll', { percent_scrolled: milestone });
            }
          }
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enabled]);
}
